import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router';
import { communicationGuard, loginGuard, logRouteChange } from './guard';
import ROUTES from '@/constants/routes';
import { STATE_MODULES } from '@/store/state-modules';
import { TechnicianGetters } from '@/store/modules/technician/getters';
import store from '@/store/technician-app-store';
import { LoginMutations } from '../store/modules/login/mutations';
import { LoginGetters } from '../store/modules/login/getters';
import utils from '../utils/utils';

const noLoginAppRoutes: Array<RouteRecordRaw> = [
	{
		path: '/',
		name: 'home',
		component: () => import('../components/views/homeNoAuth/index.vue'),
		redirect: '/jobs/xyz/details',
		beforeEnter: loginGuard,
		children: [
			{
				path: 'jobs',
				name: 'jobs',
				component: () => import('../components/views/home/Jobs/index.vue'),
				children: [
					{
						path: ':job_uuid',
						name: 'job',
						component: () => import('../components/views/home/Jobs/Job/index.vue'),
						children: [
							{
								path: 'details',
								name: 'job_details',
								component: () => import('../components/views/home/Jobs/Job/details.vue')
							},
							{
								path: 'management',
								name: 'job_managment',
								component: () => import('../components/views/home/Jobs/Job/manage.vue')
							}
						]
					}
				]
			}
		]
	}
]

const routes: Array<RouteRecordRaw> = [
	{
		path: ROUTES.LOGIN,
		name: 'login-screen',
		component: () => import('../components/views/login/index.vue'),
		children: [
			{
				path: '',
				name: 'login',
				component: () => import('../components/views/login/Login.vue')
			},
			{
				path: 'verification',
				name: 'login-verification',
				beforeEnter: communicationGuard,
				component: () => import('../components/views/login/LoginVerification.vue')
			}
		]
	},
	{
		path: '/',
		name: 'home',
		beforeEnter: loginGuard,
		component: () => import('../components/views/home/index.vue'),
		redirect: '/jobs',
		children: [
			{
				path: 'preferences',
				name: 'preferences',
				component: () => import('../components/views/home/Preferences/index.vue'),
				children: [
					{
						path: '',
						name: 'preferences_screen',
						component: () => import('../components/views/home/Preferences/Preferences.vue')
					},
					{
						path: 'leads-top-up',
						name: 'leads-top-up',
						component: () => import('../components/views/home/Preferences/leads-top-up.vue')
					}
				]
			},
			{
				path: "jobs",
				name: 'jobs',
				component: () => import('../components/views/home/Jobs/index.vue'),
				children: [
					{
						path: '',
						name: 'jobs_screen',
						component: () => import('../components/views/home/Jobs/Jobs.vue')
					},
					{
						path: ':job_uuid',
						name: 'job',
						component: () => import('../components/views/home/Jobs/Job/index.vue'),
						children: [
							{
								path: 'details',
								name: 'job_details',
								component: () => import('../components/views/home/Jobs/Job/details.vue')
							},
							{
								path: 'management',
								name: 'job_managment',
								component: () => import('../components/views/home/Jobs/Job/manage.vue')
							}
						]
					}
				]
			}
		]
	},
	{
		path: '/sign-up',
		name: 'sign-up',
		redirect: '/sign-up/details',
		component: () => import('../components/views/signup/index.vue'),
		children: [
			{
				path: 'details',
				name: 'signup-details',
				component: () => import('../components/views/signup/Details.vue'),
				beforeEnter: (to, from, next) => {
					const isLoggedIn = store.getters[`${STATE_MODULES.LOGIN}/${LoginGetters.IS_LOGGED_IN}`]
					if (isLoggedIn) {
						store.commit(`${STATE_MODULES.LOGIN}/${LoginMutations.CLEAR_TOKEN}`);
						utils.removeUserCookies()
					}
					next();
				}
			},
			{
				path: 'skills',
				name: 'signup-skills',
				beforeEnter: () => {
					const validAddressData = store.getters[`${STATE_MODULES.TECHNICIAN}/${TechnicianGetters.IS_VALID_ADDRESS_DATA}`];
					return validAddressData || '/sign-up/details'
				},
				component: () => import('../components/views/signup/Skills.vue')
			},
			{
				path: 'service-areas',
				name: 'signup-service-areas',
				beforeEnter: () => {
					const validAddressData = store.getters[`${STATE_MODULES.TECHNICIAN}/${TechnicianGetters.HAS_SELECTED_SKILL}`];
					return validAddressData || '/sign-up/skills'
				},
				component: () => import('../components/views/signup/ServiceAreas.vue')
			}
		]
	}
]

class AppRouter {
	private router: any = null;
	init (options: any) {
		const router = createRouter({
			history: createWebHistory(process.env.BASE_URL),
			routes: options.jobsApp ? noLoginAppRoutes : routes,
			scrollBehavior () {
				return { top: 0 };
			}
		});
		
		router.beforeEach((to, _, next) => {
			logRouteChange(to);
			next();
		});

		this.router = router;
	}

	get instance () {
		if (!this.router) {
			throw new Error("Router not inited!!!");
		}
		return this.router
	}
}

export default new AppRouter();
